import React, { useState, useEffect } from 'react';
import ReactSelect from "react-select";
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

import { profileAPI, masterAPI } from '../services/api';

import { toast } from '../hooks/use-toast';
import { Loader2, Plus, X, Edit, Trash2, Camera, Briefcase, GraduationCap } from 'lucide-react';
import { profileFormConfig } from "../config/profileFormConfig";
import DynamicFormSection from "../components/common/DynamicFormSection";
import DepartmentRoleSelector from "../components/common/DepartmentRoleSelector";
import DepartmentRolePicker from "../components/common/DepartmentRolePicker";

function EditProfile() {

  const { email } = useParams();
  const navigate = useNavigate();

  // Auth protection
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const role = localStorage.getItem('userRole');

    if (!token || role !== 'job_seeker') {
      localStorage.clear();
      navigate('/login');
    }
  }, [navigate]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Master dataset states
  const [cities, setCities] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [noticePeriods, setNoticePeriods] = useState([]);
  const [degrees, setDegrees] = useState([]);
  const [courses, setCourses] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [experienceYears, setExperienceYears] = useState([]);
  const [experienceMonths, setExperienceMonths] = useState([]);
  const [salaryLakhs, setSalaryLakhs] = useState([]);
  const [salaryThousands, setSalaryThousands] = useState([]);
  const masters = {
    experienceYears,
    experienceMonths,
    salaryLakhs,
    salaryThousands,
    noticePeriods
  };

  // ================= EXTRA MASTER STATES =================
  // Education extra
  const [startYears, setStartYears] = useState([]);
  const [endYears, setEndYears] = useState([]);
  const [grades, setGrades] = useState([]);

  // Role system
  const [departments, setDepartments] = useState([]);
  const [roleCategories, setRoleCategories] = useState([]);
  const [roleSubCategories, setRoleSubCategories] = useState([]);
  const [roles, setRoles] = useState([]);
  const [designations, setDesignations] = useState([]);

  // Skills master
  const [skillsMaster, setSkillsMaster] = useState([]);


  // ================= FILTER STATES =================
  // Education filter
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [filteredSpecializations, setFilteredSpecializations] = useState([]);

  // Role filter
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubCategory, setSelectedSubCategory] = useState("");
  const [selectedRole, setSelectedRole] = useState("");

  const [filteredRoleCategories, setFilteredRoleCategories] = useState([]);
  const [filteredRoleSubCategories, setFilteredRoleSubCategories] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);

  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    location: '',
    dateOfBirth: '',
    resumeHeadline: '',

    experienceYears: '',
    experienceMonths: '',

    currentRole: '',
    currentCompany: '',

    currentLakh: '',
    currentThousand: '',

    expectedLakh: '',
    expectedThousand: '',

    noticePeriod: '',
    industry: '',

    skills: [],
    education: [],
    workExperience: [],

    bio: '',
    linkedinUrl: '',
    portfolioUrl: '',
    photoUrl: '',
  });

  const getImageUrl = (url) => {
    // 🔹 Handle null, undefined, non-string
    if (!url || typeof url !== "string") {
      return "/default-avatar.png";
    }

    // 🔹 Trim spaces
    const cleanUrl = url.trim();
    if (!cleanUrl) return "/default-avatar.png";

    // 🔹 Already full URL
    if (cleanUrl.startsWith("http")) return cleanUrl;

    // 🔹 Ensure proper path
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    return `${backendUrl}${cleanUrl.startsWith("/") ? cleanUrl : `/${cleanUrl}`}`;
  };

  const [skillInput, setSkillInput] = useState('');

  // Employment modal state
  const [employmentModalOpen, setEmploymentModalOpen] = useState(false);
  const [editingEmployment, setEditingEmployment] = useState(null);
  const [employmentForm, setEmploymentForm] = useState({
    company: '',
    role: '',
    startDate: '',
    endDate: '',
    current: false,
    description: '',
    skills: [],
  });

  // Education modal state
  const [educationModalOpen, setEducationModalOpen] = useState(false);
  const [editingEducation, setEditingEducation] = useState(null);
  const [educationForm, setEducationForm] = useState({
    degree: '',
    course: '',
    institution: '',
    fieldOfStudy: '',
    startYear: '',
    endYear: '',
    grade: '',
  });

  // Load profile
  useEffect(() => {
    loadMasters();
  }, []);

  useEffect(() => {
    console.log("FORM DATA UPDATED 👉", formData);
  }, [formData]);

  // 🔥 ADD HERE (THIS BLOCK)
  // ================= FETCH PROFILE (AFTER ALL MASTERS READY) =================
  useEffect(() => {
    if (experienceYears.length > 0 &&
      experienceMonths.length > 0 && // ✅ ADD
      salaryLakhs.length > 0 &&
      salaryThousands.length > 0 && // ✅ ADD
      noticePeriods.length > 0) {
      console.log("🔥 All masters ready → fetching profile");
      fetchProfile();
    }
  }, [
    email,
    experienceYears,
    experienceMonths, // ✅ ADD
    salaryLakhs,
    salaryThousands, // ✅ ADD
    noticePeriods
  ]);

  const normalize = (v) => String(v ?? "").trim();

  const loadMasters = async () => {
    try {
      const res = await masterAPI.getAll();
      const data = res?.data || {};

      // ================= BASIC =================
      setCities(data.cities || []);
      setIndustries(data.industries || []);

      // ================= EDUCATION =================
      setDegrees(data.degrees || []);
      setCourses(data.courses || []);
      setSpecializations(data.specializations || []);

      setStartYears(data.startYears || []);
      setEndYears(data.endYears || []);
      setGrades(data.grades || []);

      // ================= ROLE =================
      setDepartments(data.departments || []);
      setRoleCategories(data.roleCategories || []);
      setRoleSubCategories(data.roleSubCategories || []);
      setRoles(data.roles || []);
      setDesignations(data.designations || []);

      // ================= EXPERIENCE =================
      setExperienceYears(
        (data.experienceYears || []).map(item => ({
          ...item,
          value: normalize(item.value)
        }))
      );

      setExperienceMonths(
        (data.experienceMonths || []).map(item => ({
          ...item,
          value: normalize(item.value)
        }))
      );

      // ================= SALARY =================
      setSalaryLakhs(
        (data.salaryLakhs || []).map(item => ({
          ...item,
          value: normalize(item.value)
        }))
      );

      setSalaryThousands(
        (data.salaryThousands || []).map(item => ({
          ...item,
          value: normalize(item.value)
        }))
      );

      // ================= NOTICE =================
      setNoticePeriods(
        (data.noticePeriods || [])
          .map(item => ({
            ...item,
            value: normalize(item.value)
          }))
          .sort((a, b) => {
            const aVal = isNaN(a.value) ? 999 : Number(a.value);
            const bVal = isNaN(b.value) ? 999 : Number(b.value);
            return aVal - bVal;
          })
      );

      // ================= SKILLS =================
      setSkillsMaster(data.skills || []);

      // ================= FILTER INIT =================
      setFilteredCourses(data.courses || []);
      setFilteredSpecializations(data.specializations || []);

      setFilteredRoleCategories(data.roleCategories || []);
      setFilteredRoleSubCategories(data.roleSubCategories || []);
      setFilteredRoles(data.roles || []);
    } catch (error) {
      console.error("❌ Error loading master data:", error);
    }
  };

  const fetchProfile = async () => {
    try {
      setLoading(true);

      const response = await profileAPI.get(email);
      const profile = response?.data || {};

      console.log("PROFILE API RESPONSE 👉", profile);

      const formattedProfile = {
        fullName: profile.fullName || "",
        phone: profile.phone || "",

        location: profile.location ?? "",

        dateOfBirth: profile.dateOfBirth
          ? profile.dateOfBirth.split("T")[0]
          : "",

        resumeHeadline: profile.resumeHeadline || "",

        // ✅ EXPERIENCE (NORMALIZED)
        experienceYears: normalize(profile.experienceYears),
        experienceMonths: normalize(profile.experienceMonths),

        // ✅ ROLE
        currentRole: profile.currentRole || "",
        currentCompany: profile.currentCompany || "",

        // ✅ SALARY (CRITICAL FIX)
        currentLakh: normalize(profile.currentSalary?.lakh),
        currentThousand: normalize(profile.currentSalary?.thousand),

        expectedLakh: normalize(profile.expectedSalary?.lakh),
        expectedThousand: normalize(profile.expectedSalary?.thousand),

        // ✅ NOTICE
        noticePeriod: normalize(profile.noticePeriod),

        industry: profile.industry || "",

        // ✅ ARRAYS SAFE
        skills: Array.isArray(profile.skills) ? profile.skills : [],
        education: Array.isArray(profile.education) ? profile.education : [],
        workExperience: Array.isArray(profile.workExperience)
          ? profile.workExperience
          : [],

        // ✅ EXTRA
        bio: profile.bio || "",
        linkedinUrl: profile.linkedinUrl || "",
        portfolioUrl: profile.portfolioUrl || "",

        // ✅ IMAGE
        photoUrl: typeof profile.photoUrl === "string"
          ? profile.photoUrl.trim()
          : "",
      };

      console.log("FORMATTED PROFILE 👉", formattedProfile);

      setFormData(prev => ({
        ...prev,
        ...formattedProfile
      }));

    } catch (error) {
      console.error("❌ Error fetching profile:", error);

      toast({
        title: "Error",
        description: "Failed to load profile",
        variant: "destructive",
      });

    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const updateField = (field) => (value) => setFormData(prev => ({ ...prev, [field]: value }));

  // ================= ADD HERE =================
  // Degree → Course
  const handleDegreeChange = (value) => {
    setEducationForm(prev => ({
      ...prev,
      degree: value,
      course: "",
      fieldOfStudy: ""
    }));

    const filtered = courses.filter(
      c => String(c.degreeLevel) === String(value)
    );

    setFilteredCourses(filtered);
    setFilteredSpecializations([]);
  };

  // Course → Specialization
  const handleCourseChange = (value) => {
    setEducationForm(prev => ({
      ...prev,
      course: value,
      fieldOfStudy: ""
    }));

    const filtered = specializations.filter(
      s => String(s.course) === String(value)
    );

    setFilteredSpecializations(filtered);
  };

  // ================= DEPARTMENT → CATEGORY =================
  const handleDepartmentChange = (value) => {
    console.log("Selected Department:", value);
    console.log("Categories:", roleCategories);

    setSelectedDepartment(value || "");

    // Reset chain
    setSelectedCategory("");
    setSelectedSubCategory("");
    setSelectedRole("");

    setFilteredRoleSubCategories([]);
    setFilteredRoles([]);

    if (!value) {
      setFilteredRoleCategories([]);
      return;
    }

    // ⚠️ SAFE FILTER (handles null / undefined department)
    const filtered = roleCategories.filter(
      (c) => c?.department &&
        String(c.department).toLowerCase().trim() ===
        String(value).toLowerCase().trim()
    );

    console.log("Filtered Categories:", filtered);

    setFilteredRoleCategories(filtered);
  };


  // ================= CATEGORY → SUBCATEGORY =================
  const handleCategoryChange = (value) => {
    setSelectedCategory(value || "");

    setSelectedSubCategory("");
    setSelectedRole("");

    setFilteredRoles([]);

    if (!value) {
      setFilteredRoleSubCategories([]);
      return;
    }

    const filtered = roleSubCategories.filter(
      (s) => s?.category &&
        String(s.category).toLowerCase().trim() ===
        String(value).toLowerCase().trim()
    );

    console.log("Filtered Subcategories:", filtered);

    setFilteredRoleSubCategories(filtered);
  };


  // ================= SUBCATEGORY → ROLE =================
  const handleSubCategoryChange = (value) => {
    setSelectedSubCategory(value || "");

    setSelectedRole("");

    if (!value) {
      setFilteredRoles([]);
      return;
    }

    const filtered = roles.filter(
      (r) => r?.subCategory &&
        String(r.subCategory).toLowerCase().trim() ===
        String(value).toLowerCase().trim()
    );

    console.log("Filtered Roles:", filtered);

    setFilteredRoles(filtered);
  };


  // ================= ROLE SELECT =================
  const handleRoleChange = (value) => {
    setSelectedRole(value || "");

    setEmploymentForm((prev) => ({
      ...prev,
      role: value || ""
    }));

    console.log("Selected Role:", value);
  };

  const handlePhotoUpload = async (e) => {

    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif"
    ];

    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Only JPG, PNG and GIF images are allowed",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Maximum allowed size is 2MB",
        variant: "destructive",
      });
      return;
    }

    try {

      setUploadingPhoto(true);

      const response = await profileAPI.uploadPhoto(email, file);

      setFormData(prev => ({
        ...prev,
        photoUrl: response.data.photoUrl
      }));

      toast({
        title: "Success",
        description: "Photo uploaded successfully",
      });

    } catch (error) {

      console.error("Photo upload error:", error);

      toast({
        title: "Upload failed",
        description: error?.response?.data?.detail || "Failed to upload photo",
        variant: "destructive",
      });

    } finally {

      setUploadingPhoto(false);

    }

  };

  const handleAddSkill = () => {

    const skill = skillInput
      .trim()
      .replace(/\b\w/g, l => l.toUpperCase());

    if (!skill) return;

    const exists = formData.skills.some(
      s => s.toLowerCase() === skill.toLowerCase()
    );

    if (exists) {
      toast({
        title: "Skill already added",
        description: skill,
      });
      return;
    }

    setFormData(prev => ({
      ...prev,
      skills: [...prev.skills, skill],
    }));

    setSkillInput("");

  };

  const handleRemoveSkill = (skillToRemove) => {

    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(
        skill => skill.toLowerCase() !== skillToRemove.toLowerCase()
      )
    }));

  };

  // ================= OPEN EMPLOYMENT MODAL =================
  const openEmploymentModal = (employment = null) => {

    if (employment) {

      setEditingEmployment(employment);

      setEmploymentForm({
        company: employment.company || "",
        role: employment.role || "",
        designation: employment.designation || "",
        startDate: employment.startDate || "",
        endDate: employment.endDate || "",
        current: employment.current || false,
        description: employment.description || "",
        skills: Array.isArray(employment.skills)
          ? employment.skills.filter(Boolean)
          : []
      });

      // 🔥 Restore role chain
      if (employment?.role) {

        setSelectedRole(employment.role);

        const roleObj = roles.find(
          r => String(r.value) === String(employment.role)
        );

        if (roleObj) {

          setSelectedSubCategory(roleObj.subCategory || "");
          setSelectedCategory(roleObj.category || "");
          setSelectedDepartment(roleObj.department || "");

          // 🔥 Restore filtered dropdowns
          setFilteredRoleCategories(
            roleCategories.filter(c => String(c.department) === String(roleObj.department)
            )
          );

          setFilteredRoleSubCategories(
            roleSubCategories.filter(s => String(s.category) === String(roleObj.category)
            )
          );

          setFilteredRoles(
            roles.filter(r => String(r.subCategory) === String(roleObj.subCategory)
            )
          );
        }
      }

    } else {

      setEditingEmployment(null);

      setEmploymentForm({
        company: "",
        role: "",
        designation: "",
        startDate: "",
        endDate: "",
        current: false,
        description: "",
        skills: []
      });

      // 🔥 Reset dropdown chain
      setSelectedDepartment("");
      setSelectedCategory("");
      setSelectedSubCategory("");
      setSelectedRole("");

      setFilteredRoleCategories([]);
      setFilteredRoleSubCategories([]);
      setFilteredRoles([]);
    }

    setEmploymentModalOpen(true);
  };

  // ================= SAVE EMPLOYMENT =================
  const saveEmployment = () => {

    // ✅ Validation
    if (!employmentForm.company?.trim() ||
      !employmentForm.role ||
      !employmentForm.startDate) {
      toast({
        title: "Error",
        description: "Company, Role and Start Date are required",
        variant: "destructive"
      });
      return;
    }

    // ✅ Prevent duplicate (safe for edit)
    const duplicate = formData.workExperience.some(
      exp => (exp.id || exp._id) !== (editingEmployment?.id || editingEmployment?._id) &&
        exp.company === employmentForm.company &&
        exp.role === employmentForm.role &&
        exp.startDate === employmentForm.startDate
    );

    if (duplicate) {
      toast({
        title: "Employment already exists",
        description: "This job entry already exists"
      });
      return;
    }

    // ✅ Prepare clean data
    const employmentData = {
      ...employmentForm,
      skills: Array.isArray(employmentForm.skills)
        ? employmentForm.skills.filter(Boolean)
        : [],
      id: editingEmployment?.id || editingEmployment?._id || Date.now().toString()
    };

    setFormData(prev => {

      let updatedExperience;

      if (editingEmployment) {

        // ✅ Update existing
        updatedExperience = prev.workExperience.map(exp => (exp.id || exp._id) === (editingEmployment.id || editingEmployment._id)
          ? employmentData
          : exp
        );

      } else {

        // ✅ Add new + SORT latest first
        updatedExperience = [
          ...prev.workExperience,
          employmentData
        ].sort(
          (a, b) => new Date(b.startDate) - new Date(a.startDate)
        );
      }

      return {
        ...prev,
        workExperience: updatedExperience
      };
    });

    setEmploymentModalOpen(false);

    toast({
      title: "Success",
      description: editingEmployment
        ? "Employment updated"
        : "Employment added"
    });
  };

  // ================= DELETE EMPLOYMENT =================
  const deleteEmployment = (id) => {

    setFormData(prev => ({
      ...prev,
      workExperience: prev.workExperience.filter(
        exp => (exp.id || exp._id) !== id
      )
    }));

    toast({
      title: "Success",
      description: "Employment deleted"
    });
  };

  // ================= OPEN EDUCATION MODAL =================
  const openEducationModal = (education = null) => {

    if (education) {

      setEditingEducation(education);

      setEducationForm({
        degree: education.degree || "",
        course: education.course || "",
        institution: education.institution || "",
        fieldOfStudy: education.fieldOfStudy || "",
        startYear: education.startYear || "",
        endYear: education.endYear || "",
        grade: education.grade || ""
      });

      // 🔥 Restore dependent dropdowns
      if (education.degree) {
        const filteredCoursesList = courses.filter(
          c => String(c.degreeLevel) === String(education.degree)
        );
        setFilteredCourses(filteredCoursesList);
      }

      if (education.course) {
        const filteredSpecs = specializations.filter(
          s => String(s.course) === String(education.course)
        );
        setFilteredSpecializations(filteredSpecs);
      }

    } else {

      setEditingEducation(null);

      setEducationForm({
        degree: "",
        course: "",
        institution: "",
        fieldOfStudy: "",
        startYear: "",
        endYear: "",
        grade: ""
      });

      // 🔥 Reset filters
      setFilteredCourses(courses || []);
      setFilteredSpecializations([]);
    }

    setEducationModalOpen(true);
  };



  // ================= SAVE EDUCATION =================
  const saveEducation = () => {

    // ✅ Required validation
    if (!educationForm.degree || !educationForm.course) {
      toast({
        title: "Error",
        description: "Please select degree and course",
        variant: "destructive"
      });
      return;
    }

    // ✅ Courses requiring specialization
    const coursesRequiringFieldOfStudy = [
      "BA",
      "B.Com (Commerce)",
      "BE/B.Tech (Engineering)",
      "B.Ed",
      "Diploma"
    ];

    if (coursesRequiringFieldOfStudy.includes(educationForm.course) &&
      !educationForm.fieldOfStudy) {
      toast({
        title: "Error",
        description: "Please select specialization",
        variant: "destructive"
      });
      return;
    }

    // ✅ Prevent duplicate (important 🔥)
    const duplicate = formData.education.some(
      edu => (edu.id || edu._id) !== (editingEducation?.id || editingEducation?._id) &&
        edu.degree === educationForm.degree &&
        edu.course === educationForm.course &&
        edu.startYear === educationForm.startYear
    );

    if (duplicate) {
      toast({
        title: "Duplicate education",
        description: "This education already exists"
      });
      return;
    }

    // ✅ Clean data
    const educationData = {
      ...educationForm,
      institution: educationForm.institution?.trim() || "",
      fieldOfStudy: educationForm.fieldOfStudy || "",
      grade: educationForm.grade || "",
      id: editingEducation?.id || editingEducation?._id || Date.now().toString()
    };

    setFormData(prev => {

      let updatedEducation;

      if (editingEducation) {

        // ✅ Update
        updatedEducation = prev.education.map(edu => (edu.id || edu._id) === (editingEducation.id || editingEducation._id)
          ? educationData
          : edu
        );

      } else {

        // ✅ Add + sort latest first
        updatedEducation = [
          ...prev.education,
          educationData
        ].sort(
          (a, b) => Number(b.endYear || 0) - Number(a.endYear || 0)
        );
      }

      return {
        ...prev,
        education: updatedEducation
      };
    });

    setEducationModalOpen(false);

    toast({
      title: "Success",
      description: editingEducation
        ? "Education updated"
        : "Education added"
    });
  };

  // ================= DELETE EDUCATION =================
  const deleteEducation = (id) => {

    setFormData(prev => ({
      ...prev,
      education: prev.education.filter(
        edu => (edu.id || edu._id) !== id
      )
    }));

    toast({
      title: "Success",
      description: "Education deleted"
    });
  };

  const calculateProfileStrength = () => {

    let score = 0;

    if (formData.fullName?.trim()) score += 10;
    if (formData.phone?.trim()) score += 10;
    if (formData.photoUrl) score += 10;
    if (formData.resumeHeadline?.trim()) score += 10;

    if (Array.isArray(formData.skills) && formData.skills.length > 0)
      score += 15;

    if (Array.isArray(formData.education) && formData.education.length > 0)
      score += 15;

    if (Array.isArray(formData.workExperience) && formData.workExperience.length > 0)
      score += 20;

    if (formData.bio?.trim()) score += 10;

    return Math.min(score, 100); // safety
  };

  const calculateTotalExperience = () => {
    let totalMonths = 0;

    (formData.workExperience || []).forEach(job => {
      if (!job?.startDate) return;

      const start = new Date(job.startDate);

      const end = job.current
        ? new Date()
        : job.endDate
          ? new Date(job.endDate)
          : new Date();

      const months = (end.getFullYear() - start.getFullYear()) * 12 +
        (end.getMonth() - start.getMonth());

      totalMonths += Math.max(0, months);
    });

    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;

    return `${years} Years ${months} Months`;
  };

  const handleSubmit = async (e) => {

    e.preventDefault();

    // ================= VALIDATION =================
    if (!formData.fullName?.trim()) {
      toast({
        title: "Error",
        description: "Full Name is required",
        variant: "destructive",
      });
      return;
    }

    try {

      setSaving(true);

      // ================= CLEAN PAYLOAD =================
      const payload = {
        // 🔹 BASIC
        fullName: formData.fullName?.trim() || "",
        phone: formData.phone?.trim() || "",
        location: formData.location || "",
        dateOfBirth: formData.dateOfBirth || "",
        resumeHeadline: formData.resumeHeadline?.trim() || "",

        // 🔹 EXPERIENCE
        experienceYears: formData.experienceYears?.toString() || "",
        experienceMonths: formData.experienceMonths?.toString() || "",

        // 🔹 ROLE
        currentRole: formData.currentRole?.trim() || "",
        currentCompany: formData.currentCompany?.trim() || "",

        // 🔥 SALARY SAFE
        currentSalary: {
          lakh: Number(formData.currentLakh) || 0,
          thousand: Number(formData.currentThousand) || 0,
        },

        expectedSalary: {
          lakh: Number(formData.expectedLakh) || 0,
          thousand: Number(formData.expectedThousand) || 0,
        },

        // 🔹 NOTICE
        noticePeriod: formData.noticePeriod?.toString() || "",

        // 🔹 ARRAYS SAFE
        skills: Array.isArray(formData.skills)
          ? formData.skills.filter(Boolean)
          : [],

        education: Array.isArray(formData.education)
          ? formData.education
          : [],

        workExperience: Array.isArray(formData.workExperience)
          ? formData.workExperience
          : [],

        // 🔹 EXTRA
        bio: formData.bio?.trim() || "",
        linkedinUrl: formData.linkedinUrl?.trim() || "",
        portfolioUrl: formData.portfolioUrl?.trim() || "",
        photoUrl: formData.photoUrl || "",
      };

      // 🔍 DEBUG
      console.log("FINAL PAYLOAD 👉", payload);

      // ================= API =================
      await profileAPI.update(email, payload);

      toast({
        title: "Success",
        description: "Profile updated successfully",
      });

      navigate(`/profile/${email}`);

    } catch (error) {

      console.error("Error updating profile:", error);

      toast({
        title: "Update failed",
        description: error?.response?.data?.detail || "Failed to update profile",
        variant: "destructive",
      });

    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-5xl mx-auto px-6 py-12">

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Edit Profile</h1>
          <p className="text-gray-600">Update your professional information</p>
        </div>

        {/* Profile Strength Meter */}

        <div className="mb-6">

          <div className="flex justify-between text-sm mb-1">
            <span>Profile Strength</span>
            <span>{calculateProfileStrength()}%</span>
          </div>

          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full"
              style={{ width: `${calculateProfileStrength()}%` }} />
          </div>

        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          <>
            {/* ================= PROFILE PHOTO ================= */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Profile Photo
              </h2>

              <div className="flex items-center gap-6">

                {/* ================= IMAGE ================= */}
                <div className="relative group">

                  <img
                    src={getImageUrl(formData.photoUrl)}
                    alt="Profile"
                    className="w-32 h-32 rounded-full object-cover border-4 border-blue-100 shadow-sm"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "/default-avatar.png";
                    } } />

                  {/* 🔥 OVERLAY (better UX) */}
                  <label
                    htmlFor="photo-upload"
                    className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition cursor-pointer"
                  >
                    {uploadingPhoto ? (
                      <Loader2 className="w-6 h-6 text-white animate-spin" />
                    ) : (
                      <Camera className="w-6 h-6 text-white" />
                    )}
                  </label>

                  {/* 🔥 SMALL FLOAT BUTTON (optional but nice) */}
                  <label
                    htmlFor="photo-upload"
                    className="absolute bottom-0 right-0 cursor-pointer"
                  >
                    <div className="w-10 h-10 bg-[#4a90e2] rounded-full flex items-center justify-center hover:bg-[#3a7bc8] transition-colors shadow-md">
                      <Camera className="w-5 h-5 text-white" />
                    </div>
                  </label>

                  {/* ✅ FILE INPUT */}
                  <Input
                    id="photo-upload"
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif"
                    className="hidden"
                    onChange={handlePhotoUpload}
                    disabled={uploadingPhoto} />
                </div>

                {/* ================= TEXT ================= */}
                <div>
                  <p className="text-sm text-gray-700 font-medium mb-1">
                    Upload a professional photo
                  </p>
                  <p className="text-xs text-gray-500">
                    JPG, PNG or GIF • Max size 2MB
                  </p>

                  {/* 🔥 OPTIONAL STATUS */}
                  {uploadingPhoto && (
                    <p className="text-xs text-blue-600 mt-2">
                      Uploading...
                    </p>
                  )}
                </div>

              </div>
            </Card>

            {/* ================= BASIC INFORMATION ================= */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Basic Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                {/* Full Name */}
                <div>
                  <Label htmlFor="fullName">Full Name *</Label>
                  <Input
                    id="fullName"
                    name="fullName"
                    required
                    value={formData.fullName || ""}
                    onChange={handleChange} />
                </div>

                {/* Phone */}
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone || ""}
                    onChange={handleChange} />
                </div>

                {/* DOB */}
                <div>
                  <Label htmlFor="dateOfBirth">Date of Birth</Label>
                  <Input
                    id="dateOfBirth"
                    name="dateOfBirth"
                    type="date"
                    value={formData.dateOfBirth || ""}
                    onChange={handleChange} />
                </div>

                {/* 🔥 LOCATION (SEARCHABLE DROPDOWN) */}
                <div>
                  <Label htmlFor="location">Location</Label>

                  <ReactSelect
                    options={cities || []}

                    value={(cities || []).find(
                      (c) => String(c.value).toLowerCase() ===
                        String(formData.location || "").toLowerCase()
                    ) || null}

                    onChange={(selected) => setFormData((prev) => ({
                      ...prev,
                      location: selected?.value || "",
                    }))}

                    placeholder="Search city..."
                    isSearchable

                    className="text-sm"

                    styles={{
                      control: (base) => ({
                        ...base,
                        borderRadius: "8px",
                        borderColor: "#d1d5db",
                        minHeight: "40px",
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                    }} />
                </div>

              </div>
            </Card>
          </>


          {/* Professional Summary */}
          <Card className="p-6">

            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Professional Summary
            </h2>

            <div className="space-y-4">

              <div>

                <Label htmlFor="resumeHeadline">
                  Resume Headline
                </Label>

                <Input
                  id="resumeHeadline"
                  name="resumeHeadline"
                  value={formData.resumeHeadline}
                  onChange={handleChange}
                  placeholder="e.g., Experienced Full Stack Developer" />

              </div>


              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                <div>

                  <Label htmlFor="currentRole">
                    Current Role / Designation
                  </Label>

                  <Input
                    id="currentRole"
                    name="currentRole"
                    value={formData.currentRole}
                    onChange={handleChange}
                    placeholder="e.g., Software Engineer" />

                </div>


                <div>

                  <Label htmlFor="currentCompany">
                    Current Company
                  </Label>

                  <Input
                    id="currentCompany"
                    name="currentCompany"
                    value={formData.currentCompany}
                    onChange={handleChange}
                    placeholder="e.g., TechCorp India" />

                </div>

              </div>

              <div>
                <Label htmlFor="industry">Industry</Label>

                <ReactSelect
                  options={industries || []}
                  value={industries.find(
                    i => i.value.toLowerCase() ===
                      (formData.industry || "").toLowerCase()
                  ) || null}
                  onChange={(selected) => setFormData(prev => ({
                    ...prev,
                    industry: selected?.value || ""
                  }))}
                  placeholder="Search Industry..."
                  isSearchable
                  className="text-sm"
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: "8px",
                      borderColor: "#d1d5db",
                      minHeight: "40px"
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999
                    })
                  }} />
              </div>

            </div>

          </Card>

          <DynamicFormSection
            title={profileFormConfig[0].title}
            fields={profileFormConfig[0].fields}
            formData={formData}
            setFormData={setFormData}
            masters={masters} />

          {/* Skills */}
          <Card className="p-6">

            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Key Skills
            </h2>

            <div className="flex gap-2 mb-3">

              <Input
                value={skillInput}
                onChange={(e) => setSkillInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddSkill();
                  }
                } }
                placeholder="Add a skill" />

              <Button
                type="button"
                onClick={handleAddSkill}
              >
                <Plus className="w-4 h-4" />
              </Button>

            </div>


            {/* Skill List */}
            <div className="flex flex-wrap gap-2">

              {formData.skills.map((skill) => (

                <Badge
                  key={skill}
                  variant="secondary"
                  className="bg-blue-50 text-blue-700 px-3 py-1 flex items-center"
                >

                  {skill}

                  <button
                    type="button"
                    onClick={() => handleRemoveSkill(skill)}
                    className="ml-2 hover:text-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>

                </Badge>

              ))}

            </div>

          </Card>

          {/* Employment */}
          <Card className="p-6">

            <div className="flex items-center justify-between mb-4">

              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <Briefcase className="w-5 h-5 mr-2 text-[#4a90e2]" />
                Employment
              </h2>

              <div className="text-sm text-gray-600">
                Total Experience: {calculateTotalExperience()}
              </div>

              <Dialog open={employmentModalOpen} onOpenChange={setEmploymentModalOpen}>

                <DialogTrigger asChild>
                  <Button type="button" onClick={() => openEmploymentModal()}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Employment
                  </Button>
                </DialogTrigger>

                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">

                  <DialogHeader>
                    <DialogTitle>
                      {editingEmployment ? "Edit Employment" : "Add Employment"}
                    </DialogTitle>
                  </DialogHeader>

                  {/* ================= FORM START ================= */}
                  <div className="space-y-4 mt-4">

                    {/* Company */}
                    <div>
                      <Label>Company *</Label>
                      <Input
                        value={employmentForm.company || ""}
                        onChange={(e) => setEmploymentForm(prev => ({
                          ...prev,
                          company: e.target.value
                        }))} />
                    </div>

                    {/* ================= DEPARTMENT & ROLE PICKER ================= */}
                    <div>
                      <Label>Department & Role *</Label>
                      <DepartmentRolePicker
                        departments={departments}
                        roleCategories={roleCategories}
                        roles={roles}
                        value={employmentForm.role || ""}
                        onChange={(picked) => {
                          setEmploymentForm(prev => ({
                            ...prev,
                            role: picked.role,
                          }));
                          // optional: filter designations by selected role
                          setSelectedRole(picked.role);
                          setSelectedCategory(picked.category);
                          setSelectedDepartment(picked.department);
                        } }
                        placeholder="Select Department & Role" />
                    </div>

                    {/* Designation (existing — keep as is) */}
                    <div>
                      <Label>Designation</Label>
                      <Select
                        value={employmentForm.designation || ""}
                        onValueChange={(value) => setEmploymentForm(prev => ({ ...prev, designation: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Designation" />
                        </SelectTrigger>
                        <SelectContent>
                          {designations.map(d => (
                            <SelectItem key={d.value} value={d.value}>
                              {d.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* ================= DATES ================= */}
                    <div className="grid grid-cols-2 gap-4">

                      <div>
                        <Label>Start Date *</Label>
                        <Input
                          type="month"
                          value={employmentForm.startDate || ""}
                          onChange={(e) => setEmploymentForm(prev => ({
                            ...prev,
                            startDate: e.target.value
                          }))} />
                      </div>

                      <div>
                        <Label>End Date</Label>
                        <Input
                          type="month"
                          value={employmentForm.endDate || ""}
                          onChange={(e) => setEmploymentForm(prev => ({
                            ...prev,
                            endDate: e.target.value
                          }))}
                          disabled={employmentForm.current} />
                      </div>

                    </div>

                    {/* Current Job */}
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={employmentForm.current || false}
                        onChange={(e) => setEmploymentForm(prev => ({
                          ...prev,
                          current: e.target.checked,
                          endDate: ""
                        }))} />
                      <Label>Currently working here</Label>
                    </div>

                    {/* ================= SKILLS ================= */}
                    <div>
                      <Label>Skills Used</Label>

                      <ReactSelect
                        isMulti
                        options={skillsMaster}

                        value={skillsMaster.filter(skill => (employmentForm.skills || []).includes(skill.value)
                        )}

                        onChange={(selected) => {
                          setEmploymentForm(prev => ({
                            ...prev,
                            skills: selected ? selected.map(s => s.value) : []
                          }));
                        } }

                        placeholder="Type skills (React, Node...)" />
                    </div>

                    {/* Description */}
                    <div>
                      <Label>Job Description</Label>
                      <Textarea
                        value={employmentForm.description || ""}
                        onChange={(e) => setEmploymentForm(prev => ({
                          ...prev,
                          description: e.target.value
                        }))}
                        rows={4} />
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-2">
                      <Button type="button" onClick={saveEmployment} className="flex-1">
                        Save
                      </Button>

                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setEmploymentModalOpen(false)}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                    </div>

                  </div>
                  {/* ================= FORM END ================= */}

                </DialogContent>
              </Dialog>

            </div>

            {/* ================= LIST ================= */}
            <div className="space-y-3">

              {formData.workExperience.map((exp) => (
                <div key={exp.id || exp._id} className="border rounded-lg p-4">

                  <div className="flex justify-between">

                    <div>
                      <h3 className="font-semibold">{exp.role}</h3>
                      <p className="text-sm text-gray-600">{exp.company}</p>
                      <p className="text-xs text-gray-500">
                        {exp.startDate} - {exp.current ? "Present" : exp.endDate}
                      </p>
                    </div>

                    <div className="flex gap-2">
                      <Button type="button" size="sm" variant="ghost" onClick={() => openEmploymentModal(exp)}>
                        <Edit className="w-4 h-4" />
                      </Button>

                      <Button type="button" size="sm" variant="ghost" onClick={() => deleteEmployment(exp.id || exp._id)}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>

                  </div>

                </div>
              ))}

              {formData.workExperience.length === 0 && (
                <p className="text-center text-gray-500 py-4">
                  No employment added yet
                </p>
              )}

            </div>

          </Card>

          {/* Education */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <GraduationCap className="w-5 h-5 mr-2 text-[#4a90e2]" />
                Education
              </h2>

              <Dialog open={educationModalOpen} onOpenChange={setEducationModalOpen}>
                <DialogTrigger asChild>
                  <Button type="button" onClick={() => openEducationModal()}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Education
                  </Button>
                </DialogTrigger>

                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>
                      {editingEducation ? "Edit Education" : "Add Education"}
                    </DialogTitle>
                  </DialogHeader>

                  <div className="space-y-4 mt-4">

                    {/* Degree + Course */}
                    <div className="grid grid-cols-2 gap-4">

                      {/* Degree */}
                      <div>
                        <Label>Degree / Level *</Label>

                        <Select
                          value={educationForm.degree || ""}
                          onValueChange={handleDegreeChange}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select degree level" />
                          </SelectTrigger>

                          <SelectContent>
                            {degrees.map((degree) => (
                              <SelectItem
                                key={degree._id || degree.value}
                                value={degree.value}
                              >
                                {degree.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Course */}
                      <div>
                        <Label>Course *</Label>

                        <Select
                          value={educationForm.course || ""}
                          onValueChange={handleCourseChange}
                          disabled={!educationForm.degree}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select course" />
                          </SelectTrigger>

                          <SelectContent className="max-h-[300px]">
                            {filteredCourses.map((course) => (
                              <SelectItem
                                key={course._id || course.value}
                                value={course.value}
                              >
                                {course.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                    </div>

                    {/* Specialization */}
                    {educationForm.course && (
                      <div>
                        <Label>Specialization</Label>

                        <Select
                          value={educationForm.fieldOfStudy || ""}
                          onValueChange={(value) => setEducationForm(prev => ({
                            ...prev,
                            fieldOfStudy: value
                          }))}
                          disabled={!educationForm.course}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select specialization" />
                          </SelectTrigger>

                          <SelectContent className="max-h-[300px]">
                            {filteredSpecializations.map((spec) => (
                              <SelectItem
                                key={spec._id || spec.value}
                                value={spec.value}
                              >
                                {spec.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Institution (OPTIONAL) */}
                    <div>
                      <Label>Institution / College</Label>

                      <Input
                        value={educationForm.institution || ""}
                        onChange={(e) => setEducationForm(prev => ({
                          ...prev,
                          institution: e.target.value
                        }))}
                        placeholder="e.g., University Name (Optional)" />
                    </div>

                    {/* Year + Grade */}
                    <div className="grid grid-cols-3 gap-4">

                      {/* Start Year */}
                      <div>
                        <Label>Start Year</Label>

                        <Select
                          value={educationForm.startYear || ""}
                          onValueChange={(value) => setEducationForm(prev => ({
                            ...prev,
                            startYear: value
                          }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Start Year" />
                          </SelectTrigger>

                          <SelectContent>
                            {startYears.map((y) => (
                              <SelectItem key={y.value} value={y.value}>
                                {y.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* End Year */}
                      <div>
                        <Label>End Year</Label>

                        <Select
                          value={educationForm.endYear || ""}
                          onValueChange={(value) => setEducationForm(prev => ({
                            ...prev,
                            endYear: value
                          }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="End Year" />
                          </SelectTrigger>

                          <SelectContent>
                            {endYears.map((y) => (
                              <SelectItem key={y.value} value={y.value}>
                                {y.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Grade Dropdown */}
                      <div>
                        <Label>Grade / CGPA / Percentage</Label>

                        <Select
                          value={educationForm.grade || ""}
                          onValueChange={(value) => setEducationForm(prev => ({
                            ...prev,
                            grade: value
                          }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select Grade" />
                          </SelectTrigger>

                          <SelectContent>
                            {grades.map((g) => (
                              <SelectItem
                                key={g._id || g.value}
                                value={g.value}
                              >
                                {g.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                    </div>

                    {/* Buttons */}
                    <div className="flex gap-2">

                      <Button
                        type="button"
                        onClick={saveEducation}
                        className="flex-1"
                      >
                        Save
                      </Button>

                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setEducationModalOpen(false)}
                        className="flex-1"
                      >
                        Cancel
                      </Button>

                    </div>

                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </Card>
          {/* ================= EDUCATION LIST ================= */}
          <Card className="p-6">

            <h2 className="text-xl font-semibold mb-4">Education</h2>

            <div className="space-y-3">

              {formData.education.map((edu) => (

                <div
                  key={edu.id || edu._id}
                  className="border rounded-lg p-4 hover:border-[#4a90e2]"
                >

                  <div className="flex justify-between">

                    <div>

                      <h3 className="font-semibold">{edu.degree}</h3>

                      {edu.course && (
                        <p className="text-sm text-blue-600">{edu.course}</p>
                      )}

                      {edu.fieldOfStudy && (
                        <p className="text-sm text-gray-600">{edu.fieldOfStudy}</p>
                      )}

                      {edu.institution && (
                        <p className="text-sm text-gray-600">{edu.institution}</p>
                      )}

                      <p className="text-xs text-gray-500">
                        {edu.startYear} - {edu.endYear}
                        {edu.grade && ` • Grade: ${edu.grade}`}
                      </p>

                    </div>

                    <div className="flex gap-2">

                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => openEducationModal(edu)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>

                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteEducation(edu.id || edu._id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>

                    </div>

                  </div>

                </div>

              ))}

              {formData.education.length === 0 && (
                <p className="text-center text-gray-500 py-4">
                  No education added yet
                </p>
              )}

            </div>

          </Card>


          {/* ================= BIO ================= */}
          <Card className="p-6">

            <h2 className="text-xl font-semibold mb-4">About You</h2>

            <Textarea
              name="bio"
              value={formData.bio || ""}
              onChange={handleChange}
              rows={4}
              placeholder="Tell recruiters about your experience..." />

          </Card>


          {/* ================= SOCIAL ================= */}
          <Card className="p-6">

            <h2 className="text-xl font-semibold mb-4">Professional Links</h2>

            <div className="grid grid-cols-2 gap-4">

              <Input
                name="linkedinUrl"
                value={formData.linkedinUrl || ""}
                onChange={handleChange}
                placeholder="LinkedIn URL" />

              <Input
                name="portfolioUrl"
                value={formData.portfolioUrl || ""}
                onChange={handleChange}
                placeholder="Portfolio URL" />

            </div>

          </Card>


          {/* ================= SUBMIT ================= */}
          <div className="flex gap-4">

            <Button type="submit" disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/profile/${email}`)}
            >
              Cancel
            </Button>

          </div>


          {/* ================= FORM CLOSE ================= */}
        </form>


        {/* ================= PAGE CLOSE ================= */}
      </div>

      <Footer />
    </div>
  );

}

export default EditProfile;